import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { WorkingDays } from "@/shared/components";
import { Button, ButtonGroup, Checkbox, DatePicker, Input, Select, TimePicker } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import { useGetRegionsQuery } from "@/shared/api/common";
import type { ContractTab, ImportWorkerFormValues } from "../types";
import { WORK_FORMAT_OPTIONS } from "../consts";

interface Props {
  activeTab: ContractTab;
  onTabChange: (tab: ContractTab) => void;
  contractNumberError?: string;
}

export default function ContractSection({ activeTab, onTabChange, contractNumberError }: Props) {
  const { t, i18n } = useTranslation("ImportWorkerPage");
  const locale = i18n.language as Locale;

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ImportWorkerFormValues>();

  const { data: regions = [] } = useGetRegionsQuery();

  const startDate = watch("start_date");
  const regionId = watch("region_id");
  const jobDutiesRu = watch("job_duties_ru") || [];
  const jobDutiesKk = watch("job_duties_kk") || [];
  const workingDaysList = watch("working_days_list");
  const workStartTime = watch("work_start_time");
  const workEndTime = watch("work_end_time");
  const hasBreak = watch("has_break");
  const breakStartTime = watch("break_start_time");
  const breakEndTime = watch("break_end_time");
  const workFormat = watch("work_format");
  const trialPeriod = watch("trial_period");

  const localeKey = `name_${locale}` as "name_ru" | "name_kk" | "name_en";
  const regionOptions = regions.map((r) => ({
    label: r[localeKey] || r.name_ru,
    value: r.id,
  }));

  useEffect(function calculateVacationDays() {
    if (!startDate) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (start >= today) {
      setValue("current_vacation_days", "0", { shouldValidate: true });
      return;
    }
    let months = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth());
    if (today.getDate() < start.getDate()) months -= 1;
    const days = Math.max(0, months) * 2.5;
    setValue("current_vacation_days", String(days), { shouldValidate: true });
  }, [startDate, setValue]);

  function handleAddDuty() {
    setValue("job_duties_ru", [...jobDutiesRu, ""], { shouldValidate: true });
    setValue("job_duties_kk", [...jobDutiesKk, ""], { shouldValidate: true });
  }

  function handleRemoveDuty(index: number) {
    setValue(
      "job_duties_ru",
      jobDutiesRu.filter((_, i) => i !== index),
      { shouldValidate: true },
    );
    setValue(
      "job_duties_kk",
      jobDutiesKk.filter((_, i) => i !== index),
      { shouldValidate: true },
    );
  }

  function handleUpdateDutyRu(index: number, value: string) {
    const updated = [...jobDutiesRu];
    updated[index] = value;
    setValue("job_duties_ru", updated, { shouldValidate: true });
  }

  function handleUpdateDutyKk(index: number, value: string) {
    const updated = [...jobDutiesKk];
    updated[index] = value;
    setValue("job_duties_kk", updated, { shouldValidate: true });
  }

  function handleRegionChange(value: number | null) {
    const region = regions.find((r) => r.id === value);
    setValue("region_id", value ?? undefined, { shouldValidate: true });
    if (region?.city_type === "national_city") {
      setValue("work_city_id", value, { shouldValidate: true });
    } else {
      setValue("work_city_id", null, { shouldValidate: true });
    }
  }

  const workFormatOptions = WORK_FORMAT_OPTIONS.map((o) => ({
    label: t(o.labelKey),
    value: o.value,
  }));

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-display-2xs content-base-primary">{t("sections.contract")}</h2>

      <div className="flex">
        <ButtonGroup
          options={[
            { label: t("contract.tabs.info"), value: "info" },
            { label: t("contract.tabs.position"), value: "position" },
            { label: t("contract.tabs.schedule"), value: "schedule" },
          ]}
          value={activeTab}
          onChange={(v) => onTabChange(v as ContractTab)}
        />
      </div>

      {activeTab === "info" && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
            <Input
              label={t("contract.contractNumber")}
              placeholder={t("contract.contractNumberPlaceholder")}
              hasOptionalLabel
              value={watch("contract_number") ?? ""}
              onChange={(e) => setValue("contract_number", e.target.value.replace(/\D/g, "").slice(0, 18), { shouldValidate: true })}
              onBlur={(e) => setValue("contract_number", e.target.value.replace(/^0+(?=\d)/, ""), { shouldValidate: true })}
              error={errors.contract_number?.message ? t(errors.contract_number.message) : contractNumberError}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
            <DatePicker
              label={t("contract.startDate")}
              placeholder={t("contract.startDatePlaceholder")}
              locale={locale}
              value={startDate}
              hasRequiredLabel
              onChange={(v) => {
                const date = v as Date | null;
                setValue("sign_date", date);
                setValue("start_date", date, { shouldValidate: true });
              }}
              error={errors.start_date?.message ? t(errors.start_date.message) : undefined}
            />

            <Select
              label={t("contract.region")}
              placeholder={t("contract.regionPlaceholder")}
              options={regionOptions}
              value={regionId ?? null}
              hasRequiredLabel
              onChange={handleRegionChange}
              error={errors.region_id?.message ? t(errors.region_id.message) : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
            <Input
              label={t("contract.employeeAddressRu")}
              placeholder={t("contract.employeeAddressRuPlaceholder")}
              hasRequiredLabel
              {...register("employee_address_ru")}
              error={errors.employee_address_ru?.message ? t(errors.employee_address_ru.message) : undefined}
            />

            <Input
              label={t("contract.employeeAddressKk")}
              placeholder={t("contract.employeeAddressKkPlaceholder")}
              hasRequiredLabel
              {...register("employee_address_kk")}
              error={errors.employee_address_kk?.message ? t(errors.employee_address_kk.message) : undefined}
            />
          </div>
        </div>
      )}

      {activeTab === "position" && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
            <Input
              label={t("contract.jobPositionRu")}
              placeholder={t("contract.jobPositionRuPlaceholder")}
              hasRequiredLabel
              {...register("job_position_ru")}
              error={errors.job_position_ru?.message ? t(errors.job_position_ru.message) : undefined}
            />

            <Input
              label={t("contract.jobPositionKk")}
              placeholder={t("contract.jobPositionKkPlaceholder")}
              hasRequiredLabel
              {...register("job_position_kk")}
              error={errors.job_position_kk?.message ? t(errors.job_position_kk.message) : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-4">
            <div className="flex flex-col gap-2">
              <label className="text-label-sm content-base-primary">
                {t("contract.jobDutiesRu")}
                <span className="content-action-negative ml-1">*</span>
              </label>
              {jobDutiesRu.map((duty, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Input
                    placeholder={t("contract.dutyPlaceholderRu")}
                    value={duty}
                    onChange={(e) => handleUpdateDutyRu(index, e.target.value)}
                    className="flex-1"
                  />
                  {jobDutiesRu.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDuty(index)}
                      className="mt-3 content-action-negative cursor-pointer hover:opacity-80 shrink-0">
                      <Trash size={16} color="currentColor" />
                    </button>
                  )}
                </div>
              ))}
              {errors.job_duties_ru && typeof errors.job_duties_ru.message === "string" && (
                <p className="text-body-regular-xs content-action-negative">{t(errors.job_duties_ru.message)}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-label-sm content-base-primary">
                {t("contract.jobDutiesKk")}
                <span className="content-action-negative ml-1">*</span>
              </label>
              {jobDutiesKk.map((duty, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Input
                    placeholder={t("contract.dutyPlaceholderKk")}
                    value={duty}
                    onChange={(e) => handleUpdateDutyKk(index, e.target.value)}
                    className="flex-1"
                  />
                  {jobDutiesKk.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDuty(index)}
                      className="mt-3 content-action-negative cursor-pointer hover:opacity-80 shrink-0">
                      <Trash size={16} color="currentColor" />
                    </button>
                  )}
                </div>
              ))}
              {errors.job_duties_kk && typeof errors.job_duties_kk.message === "string" && (
                <p className="text-body-regular-xs content-action-negative">{t(errors.job_duties_kk.message)}</p>
              )}
            </div>

            <div className="col-span-2">
              <Button type="button" variant="secondary" size="md" onClick={handleAddDuty} className="w-full">
                <Add size={16} color="currentColor" />
                {t("contract.addDuty")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "schedule" && (
        <div className="flex flex-col gap-6">
          <WorkingDays
            label={t("contract.workingDays")}
            value={workingDaysList}
            onChange={(days) => setValue("working_days_list", days, { shouldValidate: true })}
            locale={locale}
            error={errors.working_days_list?.message ? t(errors.working_days_list.message) : undefined}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
            <TimePicker
              label={t("contract.workStartTime")}
              hasRequiredLabel
              value={workStartTime}
              locale={locale}
              onChange={(v) => setValue("work_start_time", v, { shouldValidate: true })}
              error={errors.work_start_time?.message ? t(errors.work_start_time.message) : undefined}
            />

            <TimePicker
              label={t("contract.workEndTime")}
              hasRequiredLabel
              value={workEndTime}
              locale={locale}
              onChange={(v) => setValue("work_end_time", v, { shouldValidate: true })}
              error={errors.work_end_time?.message ? t(errors.work_end_time.message) : undefined}
            />
          </div>

          <Checkbox
            label={t("contract.hasBreak")}
            state={hasBreak ? "checked" : "unchecked"}
            onChange={() => setValue("has_break", !hasBreak, { shouldValidate: true })}
          />

          {hasBreak && (
            <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
              <TimePicker
                label={t("contract.breakStartTime")}
                hasRequiredLabel
                value={breakStartTime}
                locale={locale}
                onChange={(v) => setValue("break_start_time", v, { shouldValidate: true })}
                error={errors.break_start_time?.message ? t(errors.break_start_time.message) : undefined}
              />

              <TimePicker
                label={t("contract.breakEndTime")}
                hasRequiredLabel
                value={breakEndTime}
                locale={locale}
                onChange={(v) => setValue("break_end_time", v, { shouldValidate: true })}
                error={errors.break_end_time?.message ? t(errors.break_end_time.message) : undefined}
              />
            </div>
          )}

          <Select
            label={t("contract.workFormat")}
            placeholder={t("contract.workFormatPlaceholder")}
            options={workFormatOptions}
            value={workFormat || null}
            hasRequiredLabel
            onChange={(v) => setValue("work_format", (v ?? "") as ImportWorkerFormValues["work_format"], { shouldValidate: true })}
            error={errors.work_format?.message ? t(errors.work_format.message) : undefined}
          />

          <Input
            label={t("contract.salaryAmount")}
            placeholder={t("contract.salaryPlaceholder")}
            onlyNumber
            hasRequiredLabel
            {...register("salary_amount")}
            error={errors.salary_amount?.message ? t(errors.salary_amount.message) : undefined}
          />

          <Checkbox
            label={t("contract.trialPeriod")}
            state={trialPeriod ? "checked" : "unchecked"}
            onChange={() => {
              const next = !trialPeriod;
              setValue("trial_period", next, { shouldValidate: true });
              if (!next) {
                setValue("trial_duration_months", undefined, { shouldValidate: true });
              }
            }}
          />

          {trialPeriod && (
            <Input
              label={t("contract.trialDurationMonths")}
              placeholder={t("contract.trialDurationPlaceholder")}
              onlyNumber
              value={watch("trial_duration_months") ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                setValue("trial_duration_months", val ? Number(val) : undefined, { shouldValidate: true });
              }}
              error={errors.trial_duration_months?.message ? t(errors.trial_duration_months.message) : undefined}
            />
          )}

          <Input
            label={t("contract.currentVacationDays")}
            placeholder={t("contract.vacationPlaceholder")}
            hasRequiredLabel
            onlyNumber
            value={watch("current_vacation_days") ?? ""}
            onChange={(e) => setValue("current_vacation_days", e.target.value, { shouldValidate: true })}
            error={errors.current_vacation_days?.message ? t(errors.current_vacation_days.message) : undefined}
          />
        </div>
      )}
    </div>
  );
}
